import { fmtAmount } from '../lib/format';
import { CountryFlag } from '../country/CountryFlag';
import { useCountryStore } from '../country/countryStore';
import { useI18nStore } from '../i18n/i18nStore';

/**
 * The balance pill in the hall header.
 *
 * <p>It names the currency instead of only symbolising it. `Intl` renders ARS in
 * `es-AR` as a bare `$` — the correct local form, and indistinguishable from a US
 * dollar to anyone who is not Argentine. An operator (or the player) reading
 * `$ 9.300` had no way to tell which market the account belongs to, and the
 * figure is three digits longer in ARS than in BRL, so the ambiguity is worst
 * exactly where it matters. Flag + ISO code answers it in the space the old
 * wallet glyph occupied.
 *
 * <p>The flag replaces that glyph rather than joining it: four elements do not
 * fit a header pill on a 360px screen, and the amount already reads as money.
 * The button keeps its `wallet` aria-label, so nothing is lost to a screen
 * reader.
 *
 * <p>Country comes from the store, which for a signed-in player is
 * `users.country_code` (`countryStore.adoptAccountCountry`) — the same fact the
 * backend derives the wallet's currency from, so the code on the pill cannot
 * disagree with the money beside it.
 */
export function WalletChip({ balance, onOpen }: { balance: number; onOpen: () => void }) {
  const t = useI18nStore((state) => state.t);
  const code = useCountryStore((state) => state.country.code);
  const fiatCurrency = useCountryStore((state) => state.country.fiatCurrency);
  return (
    <button className="ds-wallet tap shrink-0" type="button" onClick={onOpen} aria-label={t('wallet')}>
      <CountryFlag code={code} size={15} />
      <span className="ds-wallet-cur">{fiatCurrency}</span>
      <b className="font-mono tabular-nums">{fmtAmount(balance)}</b>
    </button>
  );
}
